/**
 * JUICE · Warranty registration + customer service forms
 * ---------------------------------------------------------------------------
 * One script for both forms (sections/juice-warranty-form, sections/juice-cs-form).
 *
 * Two delivery modes, chosen by the theme setting "Form backend URL":
 *   endpoint  The form posts JSON to the Google Apps Script in /cs-forms. Photos
 *             are compressed in the browser first, then saved to a private Drive
 *             folder, logged in the Google Sheet and attached to the email that
 *             lands in customerservices@juice.co.uk.
 *   native    No backend set, or it failed and the customer chose to send anyway.
 *             The same <form> submits to Shopify's own contact form, which emails
 *             the store contact address. Text only, no photos.
 *
 * Markup contract (see the two sections):
 *   [data-juice-cs="warranty|cs"]            root, carries data-endpoint etc.
 *   [data-key="name"]                        a field the script reads
 *   [data-show-when="reason=faulty|returns"] shown only while the rule holds
 *   [data-required-when="bought_from!=juice"] required only while the rule holds
 *   Rules: a=b|c (any of), a!=b, a=* (anything), joined with & (and) or || (or).
 *   Hidden groups disable their inputs, so neither the browser's validation nor
 *   Shopify's email ever sees a field the customer couldn't.
 * ---------------------------------------------------------------------------
 */
(() => {
  if (window.__juiceCsForms) return;
  window.__juiceCsForms = true;

  const MAX_EDGE = 1600;             // px, long edge after compression
  const QUALITY = 0.8;               // JPEG quality after compression
  const MAX_RAW = 20 * 1024 * 1024;  // refuse anything bigger than this before compressing
  const MAX_PASSTHROUGH = 8 * 1024 * 1024; // PDFs, or images the browser can't decode
  const DRAFT_TTL = 24 * 60 * 60 * 1000;
  const KLAVIYO_REVISION = '2024-10-15';
  const TIMEOUT_MS = 60000;

  const readTaxonomy = () => {
    try {
      return JSON.parse(document.querySelector('[data-juice-cs-taxonomy]')?.textContent || '{}');
    } catch (e) {
      return {};
    }
  };

  const uid = () =>
    (window.crypto && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

  const loadImage = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('decode'));
      };
      img.src = url;
    });

  /**
   * A phone photo is 3 to 5 MB. Resized to 1600px at 80% it is about 300 KB and
   * still shows a frayed cable or a swollen cell clearly. The resize also turns
   * iPhone HEIC photos into JPEG, so customer service never gets a file it can't open.
   */
  const prepareFile = async (file, allowPdf) => {
    const type = (file.type || '').toLowerCase();
    const isPdf = type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (isPdf) {
      if (!allowPdf) throw new Error('type');
      if (file.size > MAX_PASSTHROUGH) throw new Error('size');
      return { blob: file, type: 'application/pdf', name: file.name, preview: '' };
    }
    if (!type.startsWith('image/') && !/\.(heic|heif|jpe?g|png|webp)$/i.test(file.name)) throw new Error('type');
    if (file.size > MAX_RAW) throw new Error('size');

    try {
      let source;
      let width;
      let height;
      if (window.createImageBitmap) {
        try {
          source = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (e) {
          source = await createImageBitmap(file);
        }
        width = source.width;
        height = source.height;
      } else {
        source = await loadImage(file);
        width = source.naturalWidth;
        height = source.naturalHeight;
      }
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      if (source.close) source.close();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
      if (!blob) throw new Error('encode');
      const name = `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;
      return { blob, type: 'image/jpeg', name, preview: URL.createObjectURL(blob) };
    } catch (e) {
      // The browser couldn't decode it (HEIC on desktop Chrome, for one). Send it as is.
      if (file.size > MAX_PASSTHROUGH) throw new Error('size');
      return { blob: file, type: type || 'image/heic', name: file.name, preview: '' };
    }
  };

  class JuiceCsForm {
    constructor(root) {
      this.root = root;
      this.kind = root.dataset.juiceCs;
      this.form = root.querySelector('form');
      if (!this.form) return;
      this.endpoint = (root.dataset.endpoint || '').trim();
      this.mode = this.endpoint ? 'endpoint' : 'native';
      this.taxonomy = readTaxonomy();
      this.files = { receipt: [], photo: [] };
      this.busy = 0;
      this.started = Date.now();
      this.submissionId = uid();
      this.redirectOverride = false;
      this.draftKey = `juice-cs-draft:${this.kind}`;

      // After a native (Shopify) submission the page reloads with the thank-you
      // message rendered server side. The draft has done its job by then.
      if (root.querySelector('[data-posted]')) {
        this.clearDraft();
        return;
      }

      this.setMode(this.mode);
      root.classList.add('jcs--ready');
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      root.querySelectorAll('input[type="date"][data-max-today]').forEach((el) => (el.max = today));
      this.bind();
      this.restoreDraft();
      this.renumber();
      this.refresh();
      this.form.querySelectorAll('[data-product-search]').forEach((input) => this.bindSearch(input));
    }

    /**
     * Shopify's storefront spam check binds to every form posting to /contact and
     * re-submits it natively, even after our handler has taken the submit over. It
     * skips forms marked data-nocaptcha="true" and checks that at submit time, so the
     * flag follows the mode: our backend has its own honeypot, timing check and rate
     * limit; the Shopify fallback keeps Shopify's protection.
     */
    setMode(mode) {
      this.mode = mode;
      this.root.dataset.mode = mode;
      if (mode === 'endpoint') this.form.dataset.nocaptcha = 'true';
      else delete this.form.dataset.nocaptcha;
    }

    /* ---------------------------------------------------------------- rules */

    value(key) {
      if (key === '__mode') return this.mode;
      if (key === '__multi') return this.rows().length > 1 ? 'yes' : 'no';
      if (key === '__redirect') return this.redirectActive() ? 'yes' : 'no';
      if (key === '__problem_category') return this.problemCategory();
      const els = this.form.querySelectorAll(`[data-key="${key}"]`);
      for (const el of els) {
        if (el.disabled) continue;
        if (el.type === 'radio') {
          if (el.checked) return el.dataset.code || el.value;
          continue;
        }
        if (el.type === 'checkbox') return el.checked ? el.dataset.code || el.value : '';
        if (el.tagName === 'SELECT') {
          const opt = el.selectedOptions[0];
          return opt ? opt.dataset.code || opt.value : '';
        }
        return el.value.trim();
      }
      return '';
    }

    test(expr) {
      if (!expr) return true;
      return expr.split('||').some((group) =>
        group.split('&').every((cond) => {
          const m = cond.trim().match(/^([\w-]+)\s*(!=|=)\s*(.*)$/);
          if (!m) return true;
          const [, key, op, list] = m;
          const v = this.value(key);
          const options = list.split('|').map((s) => s.trim());
          const hit = options.includes(v) || (options.includes('*') && v !== '');
          return op === '=' ? hit : !hit;
        })
      );
    }

    refresh() {
      // Two passes: a group can depend on a field that the first pass only just revealed.
      for (let pass = 0; pass < 2; pass++) {
        this.form.querySelectorAll('[data-show-when]').forEach((el) => {
          el.hidden = !this.test(el.dataset.showWhen);
        });
        this.form.querySelectorAll('input, select, textarea').forEach((el) => {
          if (el.dataset.alwaysOn !== undefined) return;
          el.disabled = Boolean(el.closest('[data-show-when][hidden]'));
        });
      }
      this.form.querySelectorAll('[data-required-when]').forEach((el) => {
        const on = this.test(el.dataset.requiredWhen);
        if (el.matches('[data-upload]')) el.dataset.required = on ? 'true' : 'false';
        else el.required = on;
        const star = el.closest('.jcs-field')?.querySelector('[data-optional]');
        if (star) star.hidden = on;
      });
      this.form.querySelectorAll('[data-label-by]').forEach((el) => {
        let map = {};
        try {
          map = JSON.parse(el.dataset.labels || '{}');
        } catch (e) {
          map = {};
        }
        el.textContent = map[this.value(el.dataset.labelBy)] || el.dataset.labelDefault || el.textContent;
      });
      this.form.querySelectorAll('[data-fault-source]').forEach((sel) => this.fillFaults(sel));
      this.fillProblemProducts();
      this.rows().forEach((row) => this.updateBatchHelp(row));
    }

    redirectActive() {
      if (this.kind !== 'cs' || this.redirectOverride) return false;
      if (!['faulty', 'returns'].includes(this.value('reason'))) return false;
      const list = (this.root.dataset.redirectRetailers || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      return list.includes(this.value('bought_from').toLowerCase());
    }

    /* --------------------------------------------------------------- faults */

    fillFaults(select) {
      const source = select.dataset.faultSource;
      const category = this.value(source);
      const faults = this.taxonomy.faults || {};
      const list = faults[category] || faults.__default || [];
      const sig = `${category}::${list.join('|')}`;
      if (select.dataset.sig === sig) return;
      select.dataset.sig = sig;
      const current = select.value;
      select.innerHTML = '';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = category ? 'Choose one' : 'Pick the product type first';
      select.append(placeholder);
      list.forEach((fault) => {
        const opt = document.createElement('option');
        opt.value = fault;
        opt.textContent = fault;
        select.append(opt);
      });
      if (list.includes(current)) select.value = current;
    }

    /* ------------------------------------------------------------- products */

    rows() {
      return [...this.form.querySelectorAll('[data-product-row]')];
    }

    rowValue(row, key) {
      const el = row.querySelector(`[data-key="${key}"]`);
      return el ? el.value.trim() : '';
    }

    problemCategory() {
      const rows = this.rows();
      if (!rows.length) return '';
      const sel = this.form.querySelector('[data-key="problem_product"]');
      const index = rows.length > 1 && sel ? Number(sel.value || 0) : 0;
      return this.rowValue(rows[index] || rows[0], 'p_category');
    }

    fillProblemProducts() {
      const sel = this.form.querySelector('[data-key="problem_product"]');
      if (!sel) return;
      const rows = this.rows();
      const current = sel.value;
      const labels = rows.map((row, i) => {
        const bits = [this.rowValue(row, 'p_category'), this.rowValue(row, 'p_product')].filter(Boolean);
        return `${i + 1}. ${bits.join(' · ') || 'Product'}`;
      });
      const sig = labels.join('|');
      if (sel.dataset.sig === sig) return;
      sel.dataset.sig = sig;
      sel.innerHTML = '';
      labels.forEach((text, i) => {
        const opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = text;
        sel.append(opt);
      });
      if (current && Number(current) < rows.length) sel.value = current;
    }

    addProduct(focus = true) {
      const list = this.form.querySelector('[data-products]');
      const tpl = this.form.querySelector('template[data-product-template]');
      if (!list || !tpl) return null;
      if (this.rows().length >= 10) return null;
      const row = tpl.content.firstElementChild.cloneNode(true);
      list.append(row);
      this.renumber();
      this.refresh();
      row.querySelectorAll('[data-product-search]').forEach((input) => this.bindSearch(input));
      if (focus) row.querySelector('input, select')?.focus();
      return row;
    }

    removeProduct(row) {
      if (this.rows().length <= 1) return;
      const next = row.nextElementSibling || row.previousElementSibling;
      row.remove();
      this.renumber();
      this.refresh();
      this.saveDraft();
      next?.querySelector('input, select')?.focus();
    }

    renumber() {
      const rows = this.rows();
      rows.forEach((row, i) => {
        const n = i + 1;
        const title = row.querySelector('[data-product-title]');
        if (title) title.textContent = rows.length > 1 ? `Product ${n}` : 'Your product';
        row.querySelectorAll('[data-name]').forEach((el) => {
          el.name = `contact[Product ${n} ${el.dataset.name}]`;
        });
        row.querySelectorAll('[data-id]').forEach((el) => {
          el.id = `${this.form.id}-${el.dataset.id}-${n}`;
        });
        row.querySelectorAll('[data-for]').forEach((el) => {
          el.htmlFor = `${this.form.id}-${el.dataset.for}-${n}`;
        });
        row.querySelectorAll('[data-controls]').forEach((el) => {
          el.setAttribute('aria-controls', `${this.form.id}-${el.dataset.controls}-${n}`);
        });
        const remove = row.querySelector('[data-remove-product]');
        if (remove) {
          remove.hidden = rows.length === 1;
          remove.setAttribute('aria-label', `Remove product ${n}`);
        }
      });
      const add = this.form.querySelector('[data-add-product]');
      if (add) add.hidden = rows.length >= 10;
    }

    updateBatchHelp(row) {
      const body = row.querySelector('[data-batch-help-body]');
      if (!body) return;
      const category = this.rowValue(row, 'p_category');
      const tpl =
        this.root.querySelector(`template[data-batch-help-for="${CSS.escape(category)}"]`) ||
        this.root.querySelector('template[data-batch-help-for="__default"]');
      const key = tpl ? tpl.dataset.batchHelpFor : '';
      if (body.dataset.shown === key) return;
      body.dataset.shown = key;
      body.innerHTML = '';
      if (tpl) body.append(tpl.content.cloneNode(true));
    }

    /* ------------------------------------------------------- product search */

    bindSearch(input) {
      if (input.dataset.bound) return;
      input.dataset.bound = '1';
      const row = input.closest('[data-product-row]') || input.closest('[data-search-scope]') || this.form;
      const scope = input.closest('[data-search-scope]') || row;
      const list = scope.querySelector('[data-suggest]');
      const handle = scope.querySelector('[data-key="p_handle"], [data-key="product_handle"]');
      if (!list) return;
      let active = -1;
      let items = [];
      let controller = null;

      const close = () => {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        active = -1;
      };

      const choose = (item) => {
        input.value = item.title;
        if (handle) handle.value = item.handle;
        const cat =
          row.querySelector('[data-key="p_category"]') ||
          [...this.form.querySelectorAll('[data-key="category"]')].find((el) => !el.disabled);
        const guess = this.categoryFor(item);
        if (cat && !cat.value && guess) cat.value = guess;
        close();
        this.refresh();
        this.saveDraft();
      };

      const render = () => {
        list.innerHTML = '';
        items.forEach((item, i) => {
          const li = document.createElement('li');
          li.id = `${input.id}-opt-${i}`;
          li.setAttribute('role', 'option');
          li.className = 'jcs-suggest__item';
          if (item.image) {
            const img = document.createElement('img');
            img.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=80`;
            img.alt = '';
            img.width = 40;
            img.height = 40;
            img.loading = 'lazy';
            li.append(img);
          }
          const span = document.createElement('span');
          span.textContent = item.title;
          li.append(span);
          li.addEventListener('mousedown', (e) => {
            e.preventDefault();
            choose(item);
          });
          list.append(li);
        });
        list.hidden = !items.length;
        input.setAttribute('aria-expanded', items.length ? 'true' : 'false');
      };

      const search = debounce(async () => {
        const q = input.value.trim();
        if (handle) handle.value = '';
        if (q.length < 2) {
          items = [];
          render();
          return;
        }
        controller?.abort();
        controller = new AbortController();
        const root = window.Shopify?.routes?.root || '/';
        const url =
          `${root}search/suggest.json?q=${encodeURIComponent(q)}` +
          '&resources[type]=product&resources[limit]=6' +
          '&resources[options][unavailable_products]=last' +
          '&resources[options][fields]=title,product_type,variants.sku,variants.title';
        try {
          const res = await fetch(url, { signal: controller.signal });
          const json = await res.json();
          items = (json?.resources?.results?.products || []).map((p) => ({
            title: p.title,
            handle: p.handle,
            type: p.type || '',
            image: p.image || p.featured_image?.url || '',
          }));
          active = -1;
          render();
        } catch (e) {
          if (e.name !== 'AbortError') {
            items = [];
            render();
          }
        }
      }, 180);

      input.addEventListener('input', search);
      input.addEventListener('keydown', (e) => {
        if (list.hidden || !items.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          active = (active + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          [...list.children].forEach((li, i) => li.setAttribute('aria-selected', String(i === active)));
          input.setAttribute('aria-activedescendant', list.children[active].id);
        } else if (e.key === 'Enter' && active >= 0) {
          e.preventDefault();
          choose(items[active]);
        } else if (e.key === 'Escape') {
          close();
        }
      });
      input.addEventListener('blur', () => setTimeout(close, 150));
    }

    categoryFor(item) {
      const title = (item.title || '').toLowerCase();
      for (const [pattern, category] of this.taxonomy.keywords || []) {
        if (new RegExp(pattern, 'i').test(title)) return category;
      }
      return (this.taxonomy.types || {})[item.type] || '';
    }

    /* -------------------------------------------------------------- uploads */

    async addFiles(zone, fileList) {
      const field = zone.dataset.upload;
      const max = Number(zone.dataset.max || 3);
      const allowPdf = zone.dataset.allowPdf !== undefined;
      this.uploadError(zone, '');
      const incoming = [...fileList];
      if (max === 1) this.files[field] = [];
      const room = max - this.files[field].length;
      if (incoming.length > room) {
        this.uploadError(zone, `You can add up to ${max} ${max === 1 ? 'file' : 'photos'}.`);
      }
      for (const file of incoming.slice(0, Math.max(room, 0))) {
        const item = { name: file.name, pending: true };
        this.files[field].push(item);
        this.renderFiles(zone);
        this.busy++;
        this.root.dataset.busy = 'true';
        try {
          Object.assign(item, await prepareFile(file, allowPdf), { pending: false });
        } catch (e) {
          this.files[field] = this.files[field].filter((x) => x !== item);
          this.uploadError(
            zone,
            e.message === 'type'
              ? `${file.name} isn't a photo${allowPdf ? ' or PDF' : ''}.`
              : `${file.name} is too big. Try a screenshot or a smaller photo.`
          );
        } finally {
          this.busy--;
          if (!this.busy) delete this.root.dataset.busy;
          this.renderFiles(zone);
        }
      }
    }

    renderFiles(zone) {
      const field = zone.dataset.upload;
      const list = zone.querySelector('[data-upload-list]');
      if (!list) return;
      list.innerHTML = '';
      this.files[field].forEach((item) => {
        const li = document.createElement('li');
        li.className = 'jcs-file';
        if (item.pending) li.classList.add('is-pending');
        if (item.preview) {
          const img = document.createElement('img');
          img.src = item.preview;
          img.alt = '';
          li.append(img);
        } else {
          const tag = document.createElement('span');
          tag.className = 'jcs-file__tag';
          tag.textContent = item.pending ? '…' : item.type === 'application/pdf' ? 'PDF' : 'IMG';
          li.append(tag);
        }
        const name = document.createElement('span');
        name.className = 'jcs-file__name';
        name.textContent = item.pending ? `Preparing ${item.name}` : item.name;
        li.append(name);
        if (!item.pending) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'jcs-file__remove';
          remove.setAttribute('aria-label', `Remove ${item.name}`);
          remove.textContent = '×';
          remove.addEventListener('click', () => {
            if (item.preview) URL.revokeObjectURL(item.preview);
            this.files[field] = this.files[field].filter((x) => x !== item);
            this.renderFiles(zone);
            zone.querySelector('input[type="file"]')?.focus();
          });
          li.append(remove);
        }
        list.append(li);
      });
      zone.classList.toggle('has-files', this.files[field].length > 0);
      const input = zone.querySelector('input[type="file"]');
      if (input) input.setCustomValidity('');
    }

    uploadError(zone, text) {
      const el = zone.querySelector('[data-upload-error]');
      if (!el) return;
      el.textContent = text;
      el.hidden = !text;
    }

    whenIdle() {
      return new Promise((resolve) => {
        const tick = () => (this.busy ? setTimeout(tick, 120) : resolve());
        tick();
      });
    }

    /* --------------------------------------------------------------- drafts */

    saveDraft() {
      try {
        const values = {};
        this.form.querySelectorAll('input[name], select[name], textarea[name]').forEach((el) => {
          if (el.type === 'file' || el.type === 'hidden' || el.name === 'form_type' || el.name === 'utf8') return;
          if (el.type === 'radio' || el.type === 'checkbox') {
            if (el.checked) values[`${el.name}::${el.value}`] = true;
            return;
          }
          if (el.value) values[el.name] = el.value;
        });
        localStorage.setItem(this.draftKey, JSON.stringify({ t: Date.now(), rows: this.rows().length, values }));
      } catch (e) {
        /* private mode or storage full: drafts are a convenience only */
      }
    }

    restoreDraft() {
      let draft = null;
      try {
        draft = JSON.parse(localStorage.getItem(this.draftKey) || 'null');
      } catch (e) {
        draft = null;
      }
      if (!draft || Date.now() - draft.t > DRAFT_TTL) {
        this.clearDraft();
        return;
      }
      for (let i = this.rows().length; i < Math.min(draft.rows || 1, 10); i++) this.addProduct(false);
      this.renumber();
      const values = draft.values || {};
      this.form.querySelectorAll('input[name], select[name], textarea[name]').forEach((el) => {
        if (el.type === 'file' || el.type === 'hidden') return;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (values[`${el.name}::${el.value}`]) el.checked = true;
        } else if (values[el.name] !== undefined) {
          el.value = values[el.name];
        }
      });
      this.refresh();
      // refresh() rebuilds dependent selects, so put their saved choice back afterwards.
      this.form.querySelectorAll('select[name]').forEach((el) => {
        if (values[el.name] !== undefined) el.value = values[el.name];
      });
    }

    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey);
      } catch (e) {
        /* ignore */
      }
    }

    /* --------------------------------------------------------------- submit */

    validate() {
      let firstUploadProblem = null;
      this.form.querySelectorAll('[data-upload]').forEach((zone) => {
        if (zone.closest('[data-show-when][hidden]')) return;
        if (zone.dataset.required === 'true' && !this.files[zone.dataset.upload].length) {
          this.uploadError(zone, zone.dataset.requiredMessage || 'Please add a photo.');
          firstUploadProblem ||= zone;
        }
      });
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return false;
      }
      if (firstUploadProblem) {
        firstUploadProblem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstUploadProblem.querySelector('input[type="file"]')?.focus({ preventScroll: true });
        return false;
      }
      return true;
    }

    displayValue(el) {
      if (el.tagName === 'SELECT') return el.selectedOptions[0]?.textContent.trim() || el.value;
      if (el.type === 'radio' || el.type === 'checkbox') return el.dataset.display || el.value;
      return el.value.trim();
    }

    async payload() {
      const fields = {};
      const ordered = [];
      this.form.querySelectorAll('[data-key]').forEach((el) => {
        if (el.disabled || el.closest('[data-product-row]')) return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        const key = el.dataset.key;
        if (key === 'hp' || fields[key] !== undefined) return;
        const code =
          el.tagName === 'SELECT' ? el.selectedOptions[0]?.dataset.code || el.value : el.dataset.code || el.value;
        fields[key] = String(code).trim();
        const label = el.dataset.label;
        const shown = this.displayValue(el);
        if (label && shown) ordered.push({ key, label, value: shown });
      });

      const products = this.rows().map((row) => ({
        category: this.rowValue(row, 'p_category'),
        product: this.rowValue(row, 'p_product'),
        product_handle: this.rowValue(row, 'p_handle'),
        batch_code: this.rowValue(row, 'p_batch').toUpperCase(),
      }));

      const files = [];
      for (const field of Object.keys(this.files)) {
        const zone = this.form.querySelector(`[data-upload="${field}"]`);
        if (!zone || zone.closest('[data-show-when][hidden]')) continue;
        for (const item of this.files[field]) {
          if (!item.blob) continue;
          files.push({ field, name: item.name, type: item.type, data: await blobToBase64(item.blob) });
        }
      }

      const hp = this.form.querySelector('[data-key="hp"]');
      return {
        v: 1,
        type: this.kind,
        submission_id: this.submissionId,
        elapsed_ms: Date.now() - this.started,
        hp: hp ? hp.value : '',
        page: location.href.split('?')[0],
        consent_marketing: Boolean(this.form.querySelector('[data-key="consent"]:checked')),
        fields,
        ordered,
        products: this.kind === 'warranty' ? products : [],
        files,
        test: this.root.dataset.testKey || new URLSearchParams(location.search).get('cs_test') || undefined,
      };
    }

    async send(payload) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      try {
        // text/plain keeps this a "simple" request: no CORS preflight, which Apps Script can't answer.
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload),
          signal: controller.signal,
          redirect: 'follow',
        });
        return await res.json();
      } finally {
        clearTimeout(timer);
      }
    }

    setSending(on) {
      this.sending = on;
      this.root.classList.toggle('is-sending', on);
      const btn = this.form.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = on;
        const label = btn.querySelector('[data-label]');
        if (label) {
          label.dataset.idle ||= label.textContent;
          label.textContent = on ? btn.dataset.sendingLabel || 'Sending…' : label.dataset.idle;
          // Google's first request after a quiet spell can take a while. Say so rather
          // than leave a silent spinner that looks stuck.
          clearTimeout(this.slowTimer);
          if (on) this.slowTimer = setTimeout(() => (label.textContent = 'Still sending, nearly there…'), 8000);
        }
      }
    }

    async onSubmit(event) {
      if (this.mode !== 'endpoint') {
        this.beforeNativeSubmit();
        return; // Shopify's own contact form (and its spam check) takes it from here.
      }
      event.preventDefault();
      if (this.sending) return;
      this.refresh();
      this.showError('');
      if (!this.validate()) return;
      this.setSending(true);
      let payload;
      try {
        await this.whenIdle();
        payload = await this.payload();
        const res = await this.send(payload);
        if (res && res.ok) {
          this.onSuccess(res.ref, payload);
          return;
        }
        this.onFailure((res && res.error) || 'server');
      } catch (e) {
        this.onFailure(e.name === 'AbortError' ? 'timeout' : 'network');
      } finally {
        this.setSending(false);
      }
    }

    onFailure(code) {
      if (code === 'email' || code === 'name') {
        const el = this.form.querySelector(`[data-key="${code}"]`);
        if (el) {
          el.setCustomValidity(code === 'email' ? 'Check your email address.' : 'Please add your name.');
          el.reportValidity();
          el.addEventListener('input', () => el.setCustomValidity(''), { once: true });
        }
        return;
      }
      if (code === 'rate_limited') {
        this.showError("We've had a few messages from you already, so we'll be in touch. No need to send it again.", false);
        return;
      }
      this.showError("That didn't send. Check your connection and try again.", true);
    }

    showError(text, offerFallback) {
      const box = this.root.querySelector('[data-jcs-error]');
      if (!box) return;
      box.querySelector('[data-jcs-error-text]').textContent = text;
      box.hidden = !text;
      const fallback = box.querySelector('[data-send-native]');
      if (fallback) fallback.hidden = !offerFallback;
      if (text) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    sendNative() {
      // The backend is unreachable. Shopify's contact form still gets the words through.
      this.setMode('native');
      this.showError('');
      this.refresh();
      if (this.form.requestSubmit) this.form.requestSubmit();
      else this.form.submit();
    }

    beforeNativeSubmit() {
      this.saveDraft();
      if (this.kind === 'warranty') {
        this.payload()
          .then((p) => this.klaviyo(p, '', true))
          .catch(() => {});
      }
    }

    onSuccess(ref, payload) {
      this.clearDraft();
      this.klaviyo(payload, ref, false);
      Object.values(this.files)
        .flat()
        .forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
      const done = this.root.querySelector('[data-jcs-success]');
      const first = (payload.fields.name || '').split(/\s+/)[0];
      done.querySelectorAll('[data-fill="ref"]').forEach((el) => (el.textContent = ref));
      done.querySelectorAll('[data-fill="email"]').forEach((el) => (el.textContent = payload.fields.email));
      done.querySelectorAll('[data-fill="first_name"]').forEach((el) => (el.textContent = first ? `, ${first}` : ''));
      done.querySelectorAll('[data-when-problem]').forEach((el) => {
        el.hidden = payload.fields.problem !== 'yes';
      });
      this.form.hidden = true;
      done.hidden = false;
      done.scrollIntoView({ behavior: 'smooth', block: 'start' });
      done.querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
    }

    /* -------------------------------------------------------------- Klaviyo */

    klaviyo(payload, ref, keepalive) {
      const company = this.root.dataset.klaviyoCompany;
      // Test submissions (?cs_test=…) never reach Klaviyo: no profile, no confirmation email.
      if (!company || this.kind !== 'warranty' || payload.test) return;
      const f = payload.fields;
      const [firstName, ...rest] = (f.name || '').trim().split(/\s+/);
      const where = f.bought_from === 'juice' ? 'juice.co.uk' : f.bought_from === 'other' ? f.retailer_other || 'Other' : f.bought_from;
      const profile = {
        email: f.email,
        first_name: firstName || undefined,
        last_name: rest.join(' ') || undefined,
        location: f.postcode ? { zip: f.postcode.toUpperCase(), country: 'United Kingdom' } : undefined,
        properties: { warranty_registered: true, warranty_bought_from: where || undefined },
      };
      const post = (url, body) =>
        fetch(`${url}?company_id=${encodeURIComponent(company)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify(body),
          keepalive,
        }).catch(() => {});

      post('https://a.klaviyo.com/client/events/', {
        data: {
          type: 'event',
          attributes: {
            properties: {
              ref,
              products: payload.products.map((p) => ({
                category: p.category,
                product: p.product || p.category,
                batch_code: p.batch_code,
              })),
              product_count: payload.products.length,
              bought_from: where,
              order_number: f.order_number || '',
              purchase_date: f.purchase_date || '',
              problem_reported: f.problem === 'yes',
              fault: f.fault || '',
            },
            metric: { data: { type: 'metric', attributes: { name: 'Registered Warranty' } } },
            profile: { data: { type: 'profile', attributes: profile } },
            unique_id: ref || payload.submission_id,
          },
        },
      });

      // Only with the box ticked. Klaviyo syncs this list into Shopify customers, which
      // is how a shop buyer becomes a Shopify customer Juice can email.
      const list = this.root.dataset.klaviyoList;
      if (payload.consent_marketing && list) {
        post('https://a.klaviyo.com/client/subscriptions/', {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: f.email, properties: { signup_source: 'warranty_registration' } },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        });
      }
    }

    /* ---------------------------------------------------------------- wiring */

    bind() {
      const onChange = (e) => {
        if (e.target.matches('[data-key="bought_from"]')) this.redirectOverride = false;
        this.refresh();
        this.saveDraftSoon();
      };
      this.saveDraftSoon = debounce(() => this.saveDraft(), 400);
      this.form.addEventListener('change', onChange);
      this.form.addEventListener('input', (e) => {
        if (e.target.matches('select, [type="radio"], [type="checkbox"]')) return;
        if (e.target.matches('[data-key="p_category"], [data-key="p_product"]')) this.fillProblemProducts();
        this.saveDraftSoon();
      });
      this.form.addEventListener('focusout', (e) => {
        const el = e.target;
        if (el.dataset?.transform === 'upper') el.value = el.value.toUpperCase().replace(/\s+/g, ' ').trim();
        if (el.dataset?.transform === 'order' && /^\d+$/.test(el.value.trim())) el.value = `#${el.value.trim()}`;
      });
      this.form.addEventListener('click', (e) => {
        const add = e.target.closest('[data-add-product]');
        if (add) {
          e.preventDefault();
          this.addProduct();
          this.saveDraft();
          return;
        }
        const remove = e.target.closest('[data-remove-product]');
        if (remove) {
          e.preventDefault();
          this.removeProduct(remove.closest('[data-product-row]'));
          return;
        }
        const cont = e.target.closest('[data-redirect-continue]');
        if (cont) {
          e.preventDefault();
          this.redirectOverride = true;
          this.refresh();
          this.form.querySelector('[data-after-redirect] input, [data-after-redirect] select, [data-after-redirect] textarea')?.focus();
        }
      });
      this.form.addEventListener('focusin', (e) => {
        if (e.target.matches('[data-product-search]')) this.bindSearch(e.target);
      });
      this.form.querySelectorAll('[data-upload]').forEach((zone) => {
        const input = zone.querySelector('input[type="file"]');
        input?.addEventListener('change', () => {
          if (input.files?.length) this.addFiles(zone, input.files);
          input.value = '';
        });
        zone.addEventListener('dragover', (e) => {
          e.preventDefault();
          zone.classList.add('is-drag');
        });
        zone.addEventListener('dragleave', () => zone.classList.remove('is-drag'));
        zone.addEventListener('drop', (e) => {
          e.preventDefault();
          zone.classList.remove('is-drag');
          if (e.dataTransfer?.files?.length) this.addFiles(zone, e.dataTransfer.files);
        });
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.root.querySelector('[data-send-native]')?.addEventListener('click', () => this.sendNative());
      this.root.querySelector('[data-retry]')?.addEventListener('click', () => {
        this.showError('');
        if (this.form.requestSubmit) this.form.requestSubmit();
      });
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-juice-cs]').forEach((root) => {
      if (root.__juiceCs) return;
      root.__juiceCs = new JuiceCsForm(root);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', boot);
})();
